import {Injectable, OnDestroy} from "@angular/core";
import {ScreenLayoutEntityModel} from "../../screen-layout-entity.model";
import {ITEM_TYPE, ScreenLayoutMenuData, TouchItemData} from "../../screen-layout.data";
import {BehaviorSubject, ReplaySubject, Subject, Subscription} from "rxjs";
import {EntityData, EntityDataStatus, EntityModelStatus} from "@fedeltapointofsale/data-client";
import {EditService} from "../../../../framework/entity/edit/edit-page/edit.service";
import {EditMode} from "../../../../framework/entity/edit/edit-page/edit-mode";
import {TranslateService} from "@ngx-translate/core";
import {FUIAlertActions, FUIAlertService, FUIStackedPageService} from "@fedeltapointofsale/ui";
import {ColorEditControlComponent} from "../../../../framework/common-controls/edit-controls/color-edit-control/color-edit-control.component";
import {ProductLineSearchPageComponent} from "../../../../catalogue/product-line/product-line-search-page/product-line-search-page.component";
import {CompositeProductSearchPageComponent} from "../../../../catalogue/composite-product/composite-product-search-page/composite-product-search-page.component";
import {PreparationModifierValuesSearchPageComponent} from "../../../../catalogue/preparation-modifier/preparation-modifier-values-search-page/preparation-modifier-values-search-page.component";
import {SizeGroupValuesSearchPageComponent} from "../../../../catalogue/size-group/size-group-values-search-page/size-group-values-search-page.component";
import {ExtendedSearchParams, SearchType} from "../../../../framework/entity/search/search-page/extended-search-params";
import {ProductLineEditPageComponent} from "../../../../catalogue/product-line/product-line-edit-page/product-line-edit-page.component";
import {EditParams} from "../../../../framework/entity/edit/edit-page/edit-params";
import {CompositeProductEditPageComponent} from "../../../../catalogue/composite-product/composite-product-edit-page/composite-product-edit-page.component";
import {SizeGroupEditPageComponent} from "../../../../catalogue/size-group/size-group-edit-page/size-group-edit-page.component";
import {PreparationModifierEditPageComponent} from "../../../../catalogue/preparation-modifier/preparation-modifier-edit-page/preparation-modifier-edit-page.component";
import {SellableSearchModel} from "../../../../catalogue/sellable/sellable-search.model";
import {SizeGroupValuesSearchModel} from "../../../../catalogue/size-group/size-group-values-search.model";
import {PreparationModifierValuesSearchModel} from "../../../../catalogue/preparation-modifier/preparation-modifier-values-search-model.service";
import {SellableData} from "../../../../catalogue/sellable/sellable.data";

@Injectable()
export class ScreenLayoutEditorModel implements OnDestroy {

    public static readonly DEFAULT_ROWS: number = 7;
    public static readonly DEFAULT_COLUMNS: number = 5;
    public static readonly DEFAULT_MENUS: number = 5;
    public static readonly MAX_MENUS_PER_PAGE: number = 15;
    public static readonly MAX_MENUS: number = 21;
    public static readonly MAX_ROWS: number = 15;
    public static readonly MAX_COLUMNS: number = 15;
    public static readonly MIN_MAINMENU_COLUMNS: number = 1;
    public static readonly MIN_SUBMENU_COLUMNS: number = 3;

    /**
     * Currently selected menu
     */
    selectedMenu: ScreenLayoutMenuData;
    selectedMenu$: Subject<ScreenLayoutMenuData> = new ReplaySubject(1);

    selectedSubMenus: TouchItemData[] = [];
    selectedSubMenus$: Subject<TouchItemData[]> = new ReplaySubject(1);

    numOfMenus: number;
    numOfMenus$: Subject<number> = new ReplaySubject(1);

    /**
     * Global course definition. Each menu optionally has a course from the definition
     */
    courseDefinitionId: number;
    courseDefinitionId$: Subject<number> = new ReplaySubject(1);


    /**
     * Currently selected item
     */
    selectedItem: TouchItemData;
    selectedItem$: Subject<TouchItemData> = new ReplaySubject(1);

    /**
     * Items that are on the current page organised for display, i.e. blanks inserted, etc
     */
    pageItems: TouchItemData[] = [];
    pageItems$: Subject<TouchItemData[]> = new ReplaySubject(1);

    menus$: Subject<ScreenLayoutMenuData[]> = new ReplaySubject(1);
    menus: ScreenLayoutMenuData[];

    /**
     * Number of columns on a menu
     */
    numOfColumns: number;
    numOfColumns$: Subject<number> = new ReplaySubject(1);

    numOfRows: number;
    numOfRows$: Subject<number> = new ReplaySubject(1);

    /**
     * Keep a list of record validators that are used to decide if an action can be executed (i.e. selecting an item, adding a menu).
     */
    recordValidators: Array<() => Promise<boolean>> = [];

    currentPage: number = 0;
    showNextPage: boolean;
    showPrevPage: boolean;

    /**
     * Known number of pages which increments as the user navigates through the pages. The actual number of pages may be
     * higher if the user hasn't visited tha pages
     */
    private knownPages: number = 0;


    /**
     * Indicates if the page only has blanks
     */
    emptyPage: boolean;

    private subscriptions: Subscription = new Subscription();

    painting: boolean;
    painting$: Subject<boolean> = new BehaviorSubject<boolean>(false);
    paintingColour: number;
    paintingForeColour: number;
    paintingFontName: string;
    paintingFontStyle: number;
    paintingFontSize: number;

    dragging: boolean;

    constructor(public model: ScreenLayoutEntityModel,
                public editService: EditService,
                private translateService: TranslateService,
                private alertService: FUIAlertService,
                private stackedPageService: FUIStackedPageService,
                private sellableSearchModel: SellableSearchModel,
                private sizeGroupValuesSearchModel: SizeGroupValuesSearchModel,
                private preparationModifierValuesSearchModel: PreparationModifierValuesSearchModel) {
        this.subscriptions.add(model.$data.subscribe(() => {
            this.menus$.next(this.model.data.configCategories);

            if (!this.editService.cloning && this.model.status === EntityModelStatus.INSERT) {
                this.model.data.configCategories = [];
                this.menus = this.model.data.configCategories;
                this.model.data.details.catColumns = ScreenLayoutEditorModel.DEFAULT_MENUS;
                this.addMenu();
            } else {
                this.subscriptions.add(this.model.$collectionLoaded("configCategories").subscribe(() => {
                    if (this.model.data.configCategories && this.model.data.configCategories.length > 0) {
                        this.selectMenu(this.model.data.configCategories[0]);
                        this.menus = this.model.data.configCategories;
                        this.menus$.next(this.menus);
                    }
                }));
            }

            this.updateNumOfMenus(this.model.data.details.catColumns);
            if (this.model.data.details.courses && this.model.data.details.courses.id) {
                this.updateCourses(this.model.data.details.courses.id);
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    updateNumOfMenus(num: number) {
        this.numOfMenus = num;
        this.model.data.details.catColumns = num;
        this.numOfMenus$.next(this.numOfMenus);
    }

    increaseNumOfMenus() {
        if (this.numOfMenus < ScreenLayoutEditorModel.MAX_MENUS_PER_PAGE) {
            this.updateNumOfMenus(this.numOfMenus + 1);
        }
    }

    decreaseNumOfMenus() {
        if (this.numOfMenus > 1) {
            this.updateNumOfMenus(this.numOfMenus - 1);
        }
    }

    selectItem(item: TouchItemData, formatWhenPainting: boolean = true) {
        this.executeIfValid(() => {
            this.selectItemInternal(item, formatWhenPainting);
        });
    }

    private selectItemInternal(item: TouchItemData, formatWhenPainting: boolean = true): void {
        this.selectedItem = item;
        if (this.selectedItem && formatWhenPainting) {
            if (this.painting) {
                this.selectedItem.colour = this.paintingColour;
                this.selectedItem.foreColour = this.paintingForeColour;
                this.selectedItem.fontName = this.paintingFontName;
                this.selectedItem.fontStyle = this.paintingFontStyle;
                this.selectedItem.fontSize = this.paintingFontSize;
            } else {
                this.paintingColour = this.selectedItem.colour;
                this.paintingForeColour = this.selectedItem.foreColour;
                this.paintingFontName = this.selectedItem.fontName;
                this.paintingFontStyle = this.selectedItem.fontStyle;
                this.paintingFontSize = this.selectedItem.fontSize;
            }
        }
        this.selectedItem$.next(this.selectedItem);
    }

    /**
     * Retrieves the list of items from the model depending on the current context. If a menu is selected then the
     * items from the menu are returned. If a sub-menu is selected, then the items from the sub-menu are returned.
     * Note that these items are the full list from the model which is not organised for display. It does not include
     * blanks and may span pages.
     */
    get currentItemsFromModel(): TouchItemData[] {
        return this.selectedSubMenu ? this.selectedSubMenu.children : this.selectedMenu.touchItems;
    }

    get selectedSubMenu(): TouchItemData {
        return this.selectedSubMenus.length ? this.selectedSubMenus[this.selectedSubMenus.length - 1] : undefined;
    }

    selectSubMenu(item: TouchItemData): void {
        this.executeIfValid(() => {
            this.selectedSubMenus.push(item);
            this.resetPage();
            this.selectedSubMenus$.next(this.selectedSubMenus);
        });
    }

    popSelectSubMenuTo(item: TouchItemData): void {
        this.executeIfValid(() => {
            while (this.selectedSubMenus.length && this.selectedSubMenus[this.selectedSubMenus.length - 1] !== item) {
                this.selectedSubMenus.pop();
            }
            this.resetPage();
            this.selectedSubMenus$.next(this.selectedSubMenus);
            this.selectItem(this.selectedSubMenu);
        });
    }

    popAllSelectSubMenu(): void {
        this.executeIfValid(() => {
            this.selectedSubMenus = [];
            this.selectedSubMenus$.next(this.selectedSubMenus);
            this.resetPage();
        });
    }

    selectMenu(menu: ScreenLayoutMenuData, formatWhenPainting: boolean = true): void {
        this.executeIfValid(() => {
            this.selectedMenu = menu;
            if (this.selectedMenu && formatWhenPainting) {
                if (this.painting) {
                    this.selectedMenu.colour = this.paintingColour;
                    this.selectedMenu.foreColour = this.paintingForeColour;
                    this.selectedMenu.fontName = this.paintingFontName;
                    this.selectedMenu.fontStyle = this.paintingFontStyle;
                    this.selectedMenu.fontSize = this.paintingFontSize;

                    return;
                } else {
                    this.paintingColour = this.selectedMenu.colour;
                    this.paintingForeColour = this.selectedMenu.foreColour;
                    this.paintingFontName = this.selectedMenu.fontName;
                    this.paintingFontStyle = this.selectedMenu.fontStyle;
                    this.paintingFontSize = this.selectedMenu.fontSize;
                }
            }
            this.selectedSubMenus = [];
            this.resetPage();
            this.selectedSubMenus$.next(this.selectedSubMenus);
        });
    }

    nextMenuPage(): void {
        this.currentPage++;
        if (this.currentPage > this.knownPages) {
            this.knownPages = this.currentPage;
        }
        this.refreshGrid();
    }

    previousMenuPage(): void {
        if (this.currentPage > 0) {
            this.currentPage--;
            this.refreshGrid();
        }
    }

    resetPage(): void {
        this.currentPage = 0;
        this.knownPages = 0;
        this.refreshGrid();
    }

    refreshGrid(): void {
        if (this.selectedMenu) {
            const fullItemList: TouchItemData[] = this.buildFullItems();
            let rows = this.selectedSubMenu ? this.selectedSubMenu.gridRows : this.selectedMenu.gridRows;
            if (!rows) {
                rows = this.model.data.details.rows | ScreenLayoutEditorModel.DEFAULT_ROWS;
            }
            if (rows > ScreenLayoutEditorModel.MAX_ROWS) {
                rows = ScreenLayoutEditorModel.MAX_ROWS;
            }

            this.numOfRows = rows;

            let cols = this.selectedSubMenu ? this.selectedSubMenu.gridColumns : this.selectedMenu.gridColumns;
            if (!cols) {
                cols = this.model.data.details.columns | ScreenLayoutEditorModel.DEFAULT_COLUMNS;
            }
            if (cols > ScreenLayoutEditorModel.MAX_COLUMNS) {
                cols = ScreenLayoutEditorModel.MAX_COLUMNS;
            }
            if (cols < this.minColumns) {
                cols = this.minColumns;
            }

            this.numOfColumns = cols;

            this.populatePage(fullItemList, rows * cols);

            this.pageItems$.next(this.pageItems);
            this.numOfColumns$.next(this.numOfColumns);
            this.numOfRows$.next(this.numOfRows);
            this.selectedMenu$.next(this.selectedMenu);
            this.selectItemInternal(this.selectedSubMenu ? this.selectedSubMenu : undefined, false);
        }
    }

    /**
     * Build a list of items from model including the blank items
     */
    private buildFullItems(): TouchItemData[] {
        const sortedItemList: TouchItemData[] = [];
        this.currentItemsFromModel.forEach(item => sortedItemList.push(item));
        sortedItemList.sort((item1, item2) => item1.listIndex - item2.listIndex);

        const fullItemList: TouchItemData[] = [];
        let numAdded: number = 0;
        sortedItemList.forEach(item => {
            while (item.listIndex > numAdded) {
                const blank: TouchItemData = this.createBlankTouchItem();
                blank.listIndex = numAdded;
                fullItemList.push(blank);
                numAdded++;
            }
            fullItemList.push(item);
            numAdded++;
        });
        return fullItemList;
    }

    private populatePage(itemList: TouchItemData[], pageSize: number): void {
        let currentPage: number = 0;
        let index: number = 0;
        while (currentPage <= this.currentPage) {
            this.pageItems = [];
            this.showPrevPage = false;
            this.showNextPage = false;
            this.emptyPage = true;

            let availableCells: number = pageSize;

            if (currentPage > 0) {
                availableCells--;
                this.showPrevPage = true;
            }

            if ((itemList.length > index + availableCells) || (currentPage < this.knownPages)) {
                availableCells--;
                this.showNextPage = true;
            }

            let numAdded: number = 0;

            while (numAdded < availableCells) {
                let item: TouchItemData = itemList[index];
                if (item) {
                    this.emptyPage = false;
                } else {
                    item = this.createBlankTouchItem();
                    item.listIndex = index;
                }
                this.pageItems.push(item);
                numAdded++;
                index++;
            }
            currentPage++;
        }

    }


    createBlankTouchItem(): TouchItemData {
        const entity: TouchItemData = <TouchItemData>EntityData.create(TouchItemData);
        entity.itemType = ITEM_TYPE.BLANK;
        return entity;
    }

    addMenu(): void {
        this.executeIfValid(() => {
            if (this.menus && this.menus.length < ScreenLayoutEditorModel.MAX_MENUS) {
                const menu: ScreenLayoutMenuData = this.newMenu();

                this.menus.push(menu);
                this.menus$.next(this.menus);
                this.selectMenu(menu);
            }
        });
    }

    newMenu(): ScreenLayoutMenuData {
        const menu: ScreenLayoutMenuData = <ScreenLayoutMenuData>EntityData.create(ScreenLayoutMenuData);

        this.translateService.get("wbo.screen-layout.common.newMenu").subscribe((translation: string) => menu.displayName = translation);
        menu.touchItems = [];
        menu.gridRows = ScreenLayoutEditorModel.DEFAULT_ROWS;
        menu.gridColumns = ScreenLayoutEditorModel.DEFAULT_COLUMNS;
        return menu;
    }

    removeMenu(menu: ScreenLayoutMenuData) {
        this.executeIfValid(() => {
            if (menu.touchItems.length != 0) {
                this.alertService.showConfirmationOkCancel(
                    "wbo.screen-layout.edit.prompt.removeMenu.heading",
                    "wbo.screen-layout.edit.prompt.removeMenu.message"
                ).subscribe((action) => {
                    if (action === FUIAlertActions.OK_ACTION) {
                        this.removeMenuInternal(menu);
                    }
                });
            } else {
                this.removeMenuInternal(menu);
            }
        });
    }

    private removeMenuInternal(menu: ScreenLayoutMenuData): void {
        let index: number = this.menus.indexOf(menu);
        if (index === -1) {
            return;
        }

        this.menus.splice(index, 1);
        this.menus$.next(this.menus);

        if (this.selectedMenu === menu) {
            if (!this.menus[index]) {
                index = this.menus.length - 1;
            }
            this.selectMenu(this.menus[index]);
        }
    }

    insertMenu(menu: ScreenLayoutMenuData) {
        this.executeIfValid(() => {
            const index: number = this.menus.indexOf(menu);
            if (index === -1) {
                return;
            }
            const newMenu: ScreenLayoutMenuData = this.newMenu();
            this.menus.splice(index, 0, newMenu);
            this.menus$.next(this.menus);
            this.selectMenu(newMenu);
        });
    }

    registerRecordValidator(recordValidator: () => Promise<boolean>): void {
        this.recordValidators.push(recordValidator);
    }

    unregisterRecordValidator(recordValidator: () => Promise<boolean>): void {
        this.recordValidators = this.recordValidators.filter(value => value !== recordValidator);
    }

    /**
     * Execute a function when there's all record validators are valid
     * @param action
     */
    executeIfValid(func: () => void): void {
        if (this.recordValidators.length === 0) {
            func();
        } else {
            Promise.all(this.recordValidators.map(value => value())).then(values => {
                if (values.findIndex(result => !result) == -1) {
                    func();
                }
            });
        }
    }

    moveItem(sourceItem: TouchItemData, targetItem: TouchItemData): void {
        if (targetItem.itemType === ITEM_TYPE.BLANK) {
            const targetIndex: number = targetItem.listIndex;
            targetItem.listIndex = sourceItem.listIndex;
            sourceItem.listIndex = targetIndex;
        } else if (sourceItem.listIndex < targetItem.listIndex) {
            const fullItemList: TouchItemData[] = this.buildFullItems();
            fullItemList.splice(targetItem.listIndex + 1, 0, sourceItem);
            fullItemList.splice(sourceItem.listIndex, 1);
            for (let index = 0; index < fullItemList.length; index++) {
                fullItemList[index].listIndex = index;
            }
        } else {
            this.removeItemInternal(sourceItem);
            if (sourceItem.itemType !== ITEM_TYPE.BLANK) {
                this.insertItemsInternal([this.createBlankTouchItem()], sourceItem.listIndex);
            }
            this.insertItemsInternal([sourceItem], targetItem.listIndex);
        }

        this.refreshGrid();
    }

    updateCourses(courseDefinitionId: number): void {
        if (this.courseDefinitionId && courseDefinitionId != this.courseDefinitionId) {
            this.model.data.configCategories.forEach(menu => {
                menu.courseId = undefined;
                this.clearSubMenusCourseId(menu.touchItems);
            });
        }
        this.courseDefinitionId = courseDefinitionId;
        this.courseDefinitionId$.next(this.courseDefinitionId);
    }

    private clearSubMenusCourseId(touchItems: TouchItemData[]): void {
        touchItems.filter(touchItem => touchItem.itemType === ITEM_TYPE.SUB_MENU).forEach(touchItem => {
            touchItem.courseId = undefined;
            this.clearSubMenusCourseId(touchItem.children);
        })
    }

    /**
     * Add item to the current menu without changing indexes
     * @param touchItem
     */
    addItem(touchItem: TouchItemData): void {
        if (this.currentItemsFromModel.indexOf(touchItem) == -1) {
            this.currentItemsFromModel.push(touchItem);
        }
    }

    /**
     * Insert items in the current menu in a specific index
     * @param touchItems
     * @param index
     */
    insertItems(touchItems: TouchItemData[], index: number): void {
        this.insertItemsInternal(touchItems, index);
        this.refreshGrid();

        this.selectItem(this.pageItems[index]);
    }

    private insertItemsInternal(touchItems: TouchItemData[], index: number): void {
        let removeBlank: boolean;
        let leftAdd: number = touchItems.length;
        let skipFirstBlank: boolean = touchItems.length === 1 && touchItems[0].itemType === ITEM_TYPE.BLANK;
        this.buildFullItems().filter(item => item.listIndex >= index)
            .some(item => {
                if (item.itemType === ITEM_TYPE.BLANK) {
                    if (!skipFirstBlank) {
                        if (removeBlank === undefined || removeBlank) {
                            removeBlank = true;
                            leftAdd--;
                            return leftAdd == 0;
                        }
                    }
                } else {
                    if (removeBlank) {
                        removeBlank = false;
                    }
                }
                skipFirstBlank = false;

                item.listIndex = item.listIndex + leftAdd;
                return false;
            });

        const existingTouchItems: TouchItemData[] = this.currentItemsFromModel;
        let i: number = index;
        touchItems.forEach(touchItem => {
            touchItem.listIndex = i++;
            if (touchItem.itemType !== ITEM_TYPE.BLANK) {
                existingTouchItems.push(touchItem);
            }
        });
    }

    removeItem(item: TouchItemData) {
        const func: () => void = () => {
            if (item.children.length != 0) {
                this.alertService.showConfirmationOkCancel(
                    "wbo.screen-layout.edit.prompt.removeSubMenu.heading",
                    "wbo.screen-layout.edit.prompt.removeSubMenu.message"
                ).subscribe((action) => {
                    if (action === FUIAlertActions.OK_ACTION) {
                        this.removeItemInternal(item);
                    }
                });
            } else {
                this.removeItemInternal(item);
            }

            this.refreshGrid();
        };

        if (this.selectedItem === item) {
            func();
        } else {
            this.executeIfValid(func);
        }
    }

    private removeItemInternal(item: TouchItemData): void {
        const existingTouchItems: TouchItemData[] = this.currentItemsFromModel;

        existingTouchItems.filter(existingTouchItem => existingTouchItem.listIndex > item.listIndex)
            .forEach(existingTouchItem => existingTouchItem.listIndex = existingTouchItem.listIndex - 1);

        const index: number = existingTouchItems.indexOf(item);
        if (index !== -1) {
            existingTouchItems.splice(index, 1);
        }
    }

    clearItem(item: TouchItemData): void {
        const func: () => void = () => {
            this.showConfirmationDeleteSubMenu(item).then((confirmed) => {
                if (confirmed) {
                    const index: number = this.currentItemsFromModel.indexOf(item);
                    if (index != -1) {
                        this.currentItemsFromModel.splice(index, 1);
                    }

                    const blankItem: TouchItemData = this.createBlankTouchItem();
                    blankItem.listIndex = item.listIndex;
                    this.pageItems.splice(item.listIndex, 1);
                    this.pageItems.splice(item.listIndex, 0, blankItem);
                    this.selectItemInternal(blankItem);

                    this.pageItems$.next(this.pageItems);
                }
            });
        }
        if (this.selectedItem === item) {
            func();
        } else {
            this.executeIfValid(func);
        }
    }

    private showConfirmationDeleteSubMenu(item: TouchItemData): Promise<boolean> {
        if (item.children.length == 0) {
            return Promise.resolve(true);
        } else {
            return new Promise<boolean>(resolve => {
                this.alertService.showConfirmationOkCancel(
                    "wbo.screen-layout.edit.prompt.clearSubMenu.heading",
                    "wbo.screen-layout.edit.prompt.clearSubMenu.message"
                ).subscribe((action) => {
                    resolve(action === FUIAlertActions.OK_ACTION);
                });
            });
        }
    }

    /**
     * Adds a blank row to the bottom of the page
     */
    addRow(): void {
        if (this.numOfRows >= ScreenLayoutEditorModel.MAX_ROWS || this.currentPage !== 0) {
            return;
        }

        for (let i = 0; i < this.numOfColumns; i++) {
            this.pageItems.push(this.createBlankTouchItem());
        }

        // Update the indexes of the items from the model
        this.updateIndexesFromPageItems(this.numOfColumns);

        if (this.selectedSubMenu) {
            this.selectedSubMenu.gridRows = this.numOfRows + 1;
        } else {
            this.selectedMenu.gridRows = this.numOfRows + 1;
        }

        this.refreshGrid();
    }

    get minColumns(): number {
        return this.selectedSubMenus.length > 0 ? ScreenLayoutEditorModel.MIN_SUBMENU_COLUMNS : ScreenLayoutEditorModel.MIN_MAINMENU_COLUMNS
    }

    removeRow(): void {
        if (this.numOfRows <= 1 || this.currentPage !== 0) {
            return;
        }

        // Get a list of items in the row
        const itemsInRow: TouchItemData[] = [];
        for (let i = 0; i < this.numOfColumns; i++) {
            itemsInRow.push(this.pageItems[this.pageItems.length - 1 - i]);
        }

        this.removeItemsFromPage(itemsInRow, "Row").then(updated => {
            if (updated) {
                if (this.selectedSubMenu) {
                    this.selectedSubMenu.gridRows = this.numOfRows - 1;
                } else {
                    this.selectedMenu.gridRows = this.numOfRows - 1;
                }

                this.refreshGrid();
            }
        });
    }

    /**
     * Adds a blank columns to the right of the page
     */
    addColumn(): void {
        if (this.numOfColumns >= ScreenLayoutEditorModel.MAX_COLUMNS || this.currentPage !== 0) {
            return;
        }

        // Get a list of items in the column
        const itemsInColumn: TouchItemData[] = this.itemsFromLastColumn;

        // Insert an item after each item in the column
        itemsInColumn.forEach(item => this.pageItems.splice(this.pageItems.indexOf(item) + 1, 0, this.createBlankTouchItem()));


        // Update the indexes of the items from the model
        this.updateIndexesFromPageItems(itemsInColumn.length);

        if (this.selectedSubMenu) {
            this.selectedSubMenu.gridColumns = this.numOfColumns + 1;
        } else {
            this.selectedMenu.gridColumns = this.numOfColumns + 1;
        }

        this.refreshGrid();
    }

    /**
     * Removes that last column from the page
     */
    removeColumn(): void {
        if (this.numOfColumns <= this.minColumns || this.currentPage !== 0) {
            return;
        }

        // Get a list of items in the column
        const itemsInColumn: TouchItemData[] = this.itemsFromLastColumn;

        this.removeItemsFromPage(itemsInColumn, "Column").then(updated => {
            if (updated) {
                if (this.selectedSubMenu) {
                    this.selectedSubMenu.gridColumns = this.numOfColumns - 1;
                } else {
                    this.selectedMenu.gridColumns = this.numOfColumns - 1;
                }

                this.refreshGrid();
            }
        });
    }

    /**
     * Retrieve the items that are on the last column of the current page
     */
    get itemsFromLastColumn(): TouchItemData[] {
        const column: number = this.numOfColumns - 1;
        const itemsInColumn: TouchItemData[] = [];
        let index: number = column;
        for (let i = 0; i < this.numOfRows; i++) {
            const item: TouchItemData = this.pageItems[index];
            if (item) {
                itemsInColumn.push(item);
                index = index + this.numOfColumns;
            }
        }

        if (this.showNextPage) {
            // Add the last item which is in the previous column due to the pagination button offsetting
            if (itemsInColumn.indexOf(this.pageItems[this.pageItems.length - 1]) === -1) {
                itemsInColumn.push(this.pageItems[this.pageItems.length - 1]);
            }
        }

        return itemsInColumn;
    }

    /**
     * Updates the indexes of the items from the model to match the items in the page
     * @param offset Offset to apply to items that are not on the page
     */
    updateIndexesFromPageItems(offset: number): void {
        this.currentItemsFromModel.forEach((item) => {
            const idx: number = this.pageItems.indexOf(item);
            if (idx === -1) {
                item.listIndex = item.listIndex + offset;
            } else {
                item.listIndex = idx;
            }
        });
    }

    /**
     * Removes items from the page. The user is warned if the items are not blank
     * @param items Items to remove
     * @param rowOrColumn "Row" or "Column" for i18n lookup
     */
    private removeItemsFromPage(items: TouchItemData[], rowOrColumn: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.confirmItemRemoval(items, rowOrColumn).then(allow => {
                if (allow) {
                    // Remove from page
                    items.forEach(item => this.pageItems.splice(this.pageItems.indexOf(item), 1));

                    // Remove from model
                    const currentItems: TouchItemData[] = this.currentItemsFromModel;
                    items.forEach(item => {
                        const idx: number = currentItems.indexOf(item);
                        if (idx !== -1) {
                            currentItems.splice(idx, 1)
                        }
                    });

                    // Update the indexes of the items from the model
                    this.updateIndexesFromPageItems(items.length * -1);

                    resolve(true);
                } else {
                    resolve(false);
                }
            });

        });

    }


    /**
     * Checks the items and prompts for confirmation if any are not blank.
     * @param items List of items to check
     * @param rowOrColumn "Row" or "Column" for i18n lookup
     */
    private confirmItemRemoval(items: TouchItemData[], rowOrColumn: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (items.find(item => item.itemType !== ITEM_TYPE.BLANK)) {

                const colours: number[] = [];

                // Highlight the effected cells
                items.forEach(item => {
                    colours.push(item.colour);
                    item.colour = ColorEditControlComponent.fromColorHex("#F4d03FFF");
                });

                this.alertService.showConfirmationOkCancel(
                    "wbo.screen-layout.edit.prompt.remove" + rowOrColumn + ".heading",
                    "wbo.screen-layout.edit.prompt.remove" + rowOrColumn + ".message"
                ).subscribe((action) => {
                    if (action === FUIAlertActions.OK_ACTION) {
                        resolve(true);
                    } else {
                        colours.forEach((colour, index) => {
                            items[index].colour = colour;
                        });
                        resolve(false);
                    }
                });
            } else {
                resolve(true);
            }
        });
    }

    startPainting(): void {
        this.painting = true;

        this.paintingColour = this.selectedItem ? this.selectedItem.colour : this.selectedMenu.colour;
        this.paintingForeColour = this.selectedItem ? this.selectedItem.foreColour : this.selectedMenu.foreColour;
        this.paintingFontName = this.selectedItem ? this.selectedItem.fontName : this.selectedMenu.fontName;
        this.paintingFontStyle = this.selectedItem ? this.selectedItem.fontStyle : this.selectedMenu.fontStyle;
        this.paintingFontSize = this.selectedItem ? this.selectedItem.fontSize : this.selectedMenu.fontSize;

        this.painting$.next(true);
    }

    stopPainting(): void {
        this.painting = false;
        this.painting$.next(false);
    }

    insertBlank(index: number): void {
        this.executeIfValid(() => {
            const touchItem: TouchItemData = <TouchItemData>EntityData.create(TouchItemData);
            touchItem.itemType = ITEM_TYPE.BLANK;
            this.insertItems([touchItem], index);
        });
    }

    insertProductLine(index: number): void {
        this.insertTouchItemFromSearch(ProductLineSearchPageComponent, ITEM_TYPE.SELLABLE, "sellableId", "name", index);
    }

    insertCompositeProduct(index: number): void {
        this.insertTouchItemFromSearch(CompositeProductSearchPageComponent, ITEM_TYPE.SELLABLE, "sellableId", "name", index);
    }

    insertExtra(index: number): void {
        this.insertTouchItemFromSearch(ProductLineSearchPageComponent, ITEM_TYPE.EXTRA, "sellableId", "name", index);
    }

    insertPreparationModifier(index: number): void {
        this.insertTouchItemFromSearch(PreparationModifierValuesSearchPageComponent, ITEM_TYPE.PREP, "metaTagValueId", "metaTagValueName", index);
    }

    insertSubMenu(index: number): void {
        this.executeIfValid(() => {
            const touchItem: TouchItemData = <TouchItemData>EntityData.create(TouchItemData, EntityDataStatus.ASSIGNED);
            touchItem.itemType = ITEM_TYPE.SUB_MENU;
            touchItem.displayName = "Sub menu";
            touchItem.identifier = 0;
            touchItem.gridColumns = ScreenLayoutEditorModel.DEFAULT_COLUMNS;
            touchItem.gridRows = ScreenLayoutEditorModel.DEFAULT_ROWS;
            this.insertItems([touchItem], index);
        });
    }

    insertSize(index: number): void {
        this.insertTouchItemFromSearch(SizeGroupValuesSearchPageComponent, ITEM_TYPE.SIZE, "id", "valueName", index);
    }

    insertTouchItemFromSearch(component: any, itemType: ITEM_TYPE, fieldIdentifier: string, fieldName: string, index: number): void {
        this.insertTouchItem(component, new ExtendedSearchParams(SearchType.MULTIPLE_SELECTION), itemType, fieldIdentifier, fieldName, index);
    }

    insertTouchItem(component: any, params: any, itemType: ITEM_TYPE, fieldIdentifier: string, fieldName: string, index: number): void {
        this.executeIfValid(() => {
            this.stackedPageService.open(component, params).subscribe((result: any) => {
                const items: any[] = Array.isArray(result) ? result : [result];
                if (items.length) {
                    this.insertItems(items.map(searchItem => {
                        const touchItem: TouchItemData = <TouchItemData>EntityData.create(TouchItemData);
                        touchItem.itemType = itemType;
                        touchItem.identifier = searchItem[fieldIdentifier];
                        touchItem.displayName = searchItem[fieldName];
                        touchItem.iconCheckRequired = true;
                        if (itemType === ITEM_TYPE.EXTRA) {
                            touchItem.applyExtraRules = true;
                        }
                        return touchItem;
                    }), index);
                }
            });
        });
    }

    insertNewProductLine(index: number): void {
        this.insertTouchItem(ProductLineEditPageComponent, new EditParams(undefined, EditMode.ADD), ITEM_TYPE.SELLABLE, "sellableId", "name", index);
    }

    async editItem(item: TouchItemData): Promise<any> {
        let id: number;
        let component: any;
        switch (item.itemType) {
            case ITEM_TYPE.SELLABLE:
                const searchSellable = await this.sellableSearchModel.findBySellableId(item.identifier);
                if (searchSellable) {
                    component = searchSellable.sellableType == SellableData.SELLABLE_TYPE_PRODUCT_LINE ? ProductLineEditPageComponent : CompositeProductEditPageComponent;
                }
                id = item.identifier;
                break;
            case ITEM_TYPE.SIZE:
                const searchSize = await this.sizeGroupValuesSearchModel.findById(item.identifier);
                if (searchSize) {
                    id = searchSize.sizeId;
                }
                component = SizeGroupEditPageComponent;
                break;
            case ITEM_TYPE.PREP:
                const searchPrep = await this.preparationModifierValuesSearchModel.findByValueId(item.identifier);
                if (searchPrep) {
                    id = searchPrep.metaTagDefinitionId;
                }
                component = PreparationModifierEditPageComponent;
                break;
            case ITEM_TYPE.EXTRA:
                component = ProductLineEditPageComponent;
                id = item.identifier;
                break;
        }
        if (component && id) {
            const editParams: EditParams = new EditParams(id, EditMode.EDIT);
            this.stackedPageService.open(component, editParams).subscribe(() => {
            });
        }
    }
}
